import React, { useContext, useEffect, useRef, useState } from "react";
import { AttributeContext } from "../AttributeContext";
import Utils from "../../../../Common/Utils";
import Label from "../../../Common/components/Label";
import { getAbsConstraintsByAttributeID, getCFConstraintsByAttributeID } from "api/Attributes";
import { TABSConstraints, TABSConstraintsWithoutValues, TABSValueConstriant, TCFConstraints, TCFConstraintsWithoutValues, TCFValueConstriant } from "../types";
import EditABS from "./EditABS";
import { ABS_SELECTION } from "../AttributeConstants";
import EditCF from "./EditCF";

type ABSProps = {
  finalABSValue: TABSConstraints,
  finalCFValue: TCFConstraints,
  setFinalABSValue: React.Dispatch<React.SetStateAction<TABSConstraints|null>>,
  setFinalCFValue: React.Dispatch<React.SetStateAction<TCFConstraints|null>>,
  attributeValueCount: number,
  title: string,
  id: string
}

export default function ABS({ finalABSValue, setFinalABSValue, finalCFValue, setFinalCFValue, attributeValueCount, title, id}: ABSProps) {
  const { editingItem } = useContext(AttributeContext);
  const [loading, setLoading] = useState<{status?: string, message?: string}>({ status: "", message: "", });
  const [absDetails, setAbsDetails] = useState<TABSConstraintsWithoutValues|null>(null);
  const [absDetailsCopy, setAbsDetailsCopy] = useState<TABSConstraintsWithoutValues|null>(null);

  const [cfDetails, setCfDetails] = useState<TCFConstraintsWithoutValues|null>(null);
  const [cfDetailsCopy, setCfDetailsCopy] = useState<TCFConstraintsWithoutValues|null>(null);

  const [hasClickedCFToggle, setHasClickedCFToggle] = useState<boolean>(false);
  const [hasClickedABSToggle, setHasClickedABSToggle] = useState<boolean>(false);

  const [valueConstraints, setValueConstraints] = useState<TABSValueConstriant[]>([]);
  const [valueCfConstraints, setValueCfConstraints] = useState<TCFValueConstriant[]>([]);

  const [attributeValuesCount, setAttributeValuesCount] = useState(attributeValueCount ?? 0);
  const [collapse, setCollapse] = useState(false);
  const [disableButtons, setDisableButtons] = useState(true);
  const [search, setSearch] = useState<string|undefined>(undefined);
  const [cfSearch, setCfSearch] = useState<string|undefined>(undefined);
  const initialLoading = useRef<boolean>(true);

  useEffect(() => {
    setAttributeValuesCount(attributeValueCount)
  }, [attributeValueCount]);

  useEffect(() => {
    setLoading({ status: "", message: "", });
    setAbsDetails(null);
    setAttributeValuesCount(0);
    setSearch(undefined);
    setCfSearch(undefined);
    setFinalABSValue(null);
    setFinalCFValue(null);
  }, [editingItem?.id]);

  useEffect(() => {
    if (JSON.stringify(absDetails) === JSON.stringify(absDetailsCopy)) {
      setDisableButtons(true);
    } else if (
      valueConstraints &&
      valueConstraints.length > 0
    ) {
      let hasValidUrl = valueConstraints.some(function (val) {
        return false;
      });
      if (hasValidUrl) {
        setDisableButtons(true);
      } else {
        setDisableButtons(false);
      }
    } else {
      setDisableButtons(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absDetails]);

  useEffect(() => {
    if(!absDetails) return

    setFinalABSValue((prev) =>  {
      const changedValues = valueConstraints.filter(value => value.changed).map((value) => value.value)

      const nextValueConstraints = [
        ...(prev?.value_constraints.filter(value => !changedValues.includes(value.value)) ?? []), 
        ...valueConstraints.filter(value => value.changed)
      ]

      return { ...absDetails,  value_constraints: nextValueConstraints}
    });
  }, [absDetails, valueConstraints]);


  useEffect(() => {
    if(!cfDetails) return;

    setFinalCFValue((prev) => {
      const changedValues = valueCfConstraints
          .filter(value => value.changed)
          .map(value => value.value);
  
      const nextValueConstraints = [
          ...(prev?.value_constraints.filter(value => !changedValues.includes(value.value)) ?? []),
          ...valueCfConstraints.filter(value => value.changed)
      ];
  
      return {  ...cfDetails, value_constraints: nextValueConstraints 
      };
  });
  
  
  }, [cfDetails, valueCfConstraints]);

  useEffect(() => {
    if (title === ABS_SELECTION) {
      if (collapse && absDetails === null) 
        fetchAbsConstraints()
        .then(() => initialLoading.current = false);
    } else {
      if (collapse && absDetails === null) 
        fetchCfConstraints()
        .then(() => initialLoading.current = false);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  async function fetchAbsConstraints(search?: string, skip?: number, reset?: boolean) {
    if(!editingItem) return setLoading({ status: "Error", message: "Unexpected error" });
    setLoading({ status: "Loading", message: "Loading ABS Details" });

    getAbsConstraintsByAttributeID(editingItem.id, 10, skip, search)
    .then(({constraints, count}) => {
      setLoading({ status: "Success", message: undefined });
      const {value_constraints, ...rest} = constraints;

      if(initialLoading.current || !absDetails) {
        if (rest.abs_enabled == null) rest.abs_enabled = false
        setAbsDetails(rest as TABSConstraintsWithoutValues);
        setAbsDetailsCopy(rest as TABSConstraintsWithoutValues);
      }
      if(value_constraints) setValueConstraints((prev) => {
        const newValueConstraints = [...value_constraints]
        if(finalABSValue?.abs_enabled !== absDetailsCopy?.abs_enabled || hasClickedABSToggle){
          newValueConstraints.forEach((value) => {
            value.abs_show = true
          })
        }

        return prev?.length > 0 && !reset ? [...prev, ...newValueConstraints] : newValueConstraints
      })
      setAttributeValuesCount(count)
      setDisableButtons(true);
    })
    .catch((err) => {
      setLoading({ status: "Error", message: "Error while loading details" });
    })
  }

  async function fetchCfConstraints(search?: string, skip?: number, reset?: boolean) {
    if(!editingItem) return setLoading({ status: "Error", message: "Unexpected error" });
    setLoading({ status: "Loading", message: "Loading CF Details" });

    getCFConstraintsByAttributeID(editingItem.id, 10, skip, search)
    .then(({constraints, count}) => {
      setLoading({ status: "Success", message: undefined });
      const {value_constraints, ...rest} = constraints;

      if(initialLoading.current || !cfDetails) {
        if (rest.cf_enabled == null) rest.cf_enabled = false
        setCfDetails(rest as TCFConstraintsWithoutValues);
        setCfDetailsCopy(rest as TCFConstraintsWithoutValues);
      }
      if(value_constraints) setValueCfConstraints((prev) => {
        const newValueConstraints = [...value_constraints]
        if(finalCFValue?.cf_enabled !== cfDetailsCopy?.cf_enabled || hasClickedCFToggle){
          newValueConstraints.forEach((value) => {
            value.cf_show = finalCFValue?.cf_enabled
          })
        }

        return prev?.length > 0 && !reset ? [...prev, ...newValueConstraints] : newValueConstraints
      })
      setAttributeValuesCount(count)
      setDisableButtons(true);
    })
    .catch((err) => {
      setLoading({ status: "Error", message: "Error while loading details" });
    })
  }


  useEffect(() => {
    if(initialLoading.current) return
    if (title === ABS_SELECTION) {
      fetchAbsConstraints(search, undefined, true);
    } else {
      fetchCfConstraints(cfSearch, undefined, true);
    }
  }, [search])

  function handle_collapse() {
    setCollapse(!collapse);
  }

  return (
    <>
      <div className="accordion" id={`accordionFlush${id}`}>
        <div className="accordion-item border-0">
          <div
            className="accordion-button bg-white collapsed"
            onClick={() => handle_collapse()}
            data-bs-toggle="collapse"
            data-bs-target={`#flush-collapse${id}`}
            aria-controls={`flush-collapse${id}`}
            style={Utils.borderStyle()}
          >
            <div>
              <Label message={title} />
            </div>
          </div>

          <div
            id={`flush-collapse${id}`}
            className="accordion-collapse collapse"
            aria-labelledby="flush-headingCat"
          >
            <div className="accordion-body p-0" style={{ minHeight: "30px" }}>
              <LoadingSuccessErrorAlert loading={loading} />
              {(loading.status === "Success" || loading.status === "Error") ?
                title === ABS_SELECTION ? (
                  <>
                    <div className="card shadow p-3 mb-3 bg-body rounded border-top-0 ">
                      {absDetails && (
                        <EditABS
                          absDetails={absDetails}
                          setAbsDetails={setAbsDetails}
                          attributeValuesCount={attributeValuesCount}
                          fetchAbsConstraints={fetchAbsConstraints}
                          valueConstraints ={valueConstraints}
                          setValueConstraints ={setValueConstraints}
                          search = {search}
                          setSearch = {setSearch}
                          hasClickedABSToggle={hasClickedABSToggle}
                          setHasClickedABSToggle={setHasClickedABSToggle}
                        />
                      )}
                    </div>
                  </>
                ) :  <>
                <div className="card shadow p-3 mb-3 bg-body rounded border-top-0 ">
                  {cfDetails && (
                    <EditCF
                      cfDetails={cfDetails}
                      setCfDetails={setCfDetails}
                      attributeValuesCount={attributeValuesCount}
                      fetchCfConstraints={fetchCfConstraints}
                      valueCfConstraints ={valueCfConstraints}
                      setValueCfConstraints ={setValueCfConstraints}
                      search = {cfSearch}
                      setSearch = {setCfSearch}
                      hasClickedCFToggle={hasClickedCFToggle} 
                      setHasClickedCFToggle={setHasClickedCFToggle}
                    />
                  )}
                </div>
              </> : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function LoadingSuccessErrorAlert({ loading }: {loading: {status?: string, message?: string}}) {
  return (
    <>
      {loading.status === "Saving" && (
        <div className="alert alert-primary bg-white small p-2" role="alert">
          <span className="fas fa-circle-notch fa-spin" />
          &nbsp;
          <strong>{loading.message}</strong>
        </div>
      )}
      {loading.status === "Loading" && (
        <div className="alert alert-primary bg-white small p-2" role="alert">
          <span className="fas fa-circle-notch fa-spin" />
          &nbsp;
          <strong>{loading.message}</strong>
        </div>
      )}
      {loading.status === "Error" && (
        <div className="alert alert-danger bg-white small p-2" role="alert">
          <span className="fas fa-exclamation-triangle" />
          &nbsp;
          <strong>{loading.message}</strong>
        </div>
      )}
      {loading.status === "Success" && loading.message !== undefined && (
        <div className="alert alert-primary bg-white small p-2" role="alert">
          <span className="far fa-check-circle" />
          &nbsp;
          <strong>{loading.message}</strong>
        </div>
      )}
    </>
  );
}
